import { useCallback, useEffect } from 'react';
import { debounce } from 'lodash';

import { MapDetailsWaypoint } from '../../types/rides';
import { HEADING_HEIGHT } from '../PageHeading/constants';

import { DEFAULT_CENTER_COORDS, INITIAL_ZOOM_LEVEL } from './RouteMap.helpers';

interface IUseRouteMapDetails {
  mapInstance?: google.maps.Map;
  orderedWaypointCoordinates: MapDetailsWaypoint[];
}

export const useMapCenter = ({ mapInstance, orderedWaypointCoordinates }: IUseRouteMapDetails) => {
  const fitMapBounds = useCallback(() => {
    if (mapInstance) {
      const waypointsCoords = orderedWaypointCoordinates;

      if (waypointsCoords?.length) {
        const bounds = new window.google.maps.LatLngBounds();

        waypointsCoords.forEach((coords) => {
          bounds.extend({
            lat: coords.lat,
            lng: coords.lng,
          });
        });
        // paddings were added in order to fit any kind of route length and orientation
        mapInstance.fitBounds(bounds, {
          top: HEADING_HEIGHT,
          right: 100,
          bottom: 100,
          left: 100,
        });
        mapInstance.panBy(0, 50);
        waypointsCoords.length <= 1 && mapInstance.setZoom(17);
      } else {
        mapInstance.setCenter(DEFAULT_CENTER_COORDS);
        mapInstance.setZoom(INITIAL_ZOOM_LEVEL);
      }
    }
  }, [mapInstance, orderedWaypointCoordinates]);

  useEffect(() => {
    debounce(fitMapBounds, 800)();
  }, [fitMapBounds, orderedWaypointCoordinates]);

  useEffect(() => {
    // this is for adjusting the bounds when the window size changed
    const handleResize = () => {
      debounce(fitMapBounds, 800)();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  return { center: DEFAULT_CENTER_COORDS };
};
